// Spaced Repetition System based on SM-2 algorithm

import { getQuestionMastery } from './analytics';

const SR_CONFIG = {
  NEW_QUESTION_WEIGHT: 3.0,      // Show new questions more often
  STRUGGLING_WEIGHT: 2.5,         // Show struggling questions frequently
  IMPROVING_WEIGHT: 1.5,          // Show improving questions moderately
  LEARNING_WEIGHT: 1.0,           // Normal weight for learning questions
  MASTERED_WEIGHT: 0.1,           // Show mastered questions rarely
};

// Days between reviews (SM-2 inspired)
const REVIEW_INTERVALS = {
  new: 0,              // Show today
  struggling: 0.5,     // Show within hours
  improving: 2,        // Show in 2 days
  learning: 4,         // Show in 4 days
  mastered: 30,        // Show in 30 days
};

// Calculate when a question should be reviewed next
function getNextReviewDate(mastery) {
  const lastAttempt = mastery.lastAttempt ? new Date(mastery.lastAttempt) : new Date();
  const intervalDays = REVIEW_INTERVALS[mastery.status] || 0;
  const nextReview = new Date(lastAttempt);
  nextReview.setDate(nextReview.getDate() + intervalDays);
  return nextReview;
}

// Check if a question is due for review
function isDueForReview(mastery) {
  const nextReview = getNextReviewDate(mastery);
  return new Date() >= nextReview;
}

// Calculate priority score for a question (higher = should show sooner)
function calculatePriority(mastery, questionId) {
  const weight = SR_CONFIG[mastery.status] || 1.0;
  const nextReview = getNextReviewDate(mastery);
  const daysOverdue = (new Date() - nextReview) / (1000 * 60 * 60 * 24);
  const urgency = Math.max(0, daysOverdue + 1); // Convert negative to 0

  return weight * urgency;
}

// Get questions prioritized by spaced repetition algorithm
export function getPrioritizedQuestions(allQuestions, options = {}) {
  const {
    includeNew = true,
    maxDueCount = 50,
    maxTotalCount = null,
  } = options;

  const now = new Date();
  const questionPriorities = [];

  allQuestions.forEach(question => {
    const mastery = getQuestionMastery(question.id);

    // Filter based on status
    if (mastery.status === 'mastered' && !options.includeMastered) {
      return; // Skip mastered questions unless explicitly included
    }

    // Calculate if due and priority
    const dueForReview = isDueForReview(mastery);
    const priority = calculatePriority(mastery, question.id);

    questionPriorities.push({
      question,
      mastery,
      priority,
      dueForReview,
    });
  });

  // Sort by priority (highest first)
  questionPriorities.sort((a, b) => b.priority - a.priority);

  // Return top questions
  let result = questionPriorities;

  if (maxTotalCount) {
    result = result.slice(0, maxTotalCount);
  }

  return result.map(item => item.question);
}

// Get questions that are due for review (overdue or due today)
export function getDueQuestions(allQuestions) {
  return allQuestions.filter(question => {
    const mastery = getQuestionMastery(question.id);
    return isDueForReview(mastery);
  });
}

// Get spaced repetition study plan for the day
export function getDailyStudyPlan(allQuestions, targetQuestionsPerDay = 20) {
  const dueQuestions = getDueQuestions(allQuestions);
  const prioritized = getPrioritizedQuestions(allQuestions, {
    maxTotalCount: targetQuestionsPerDay,
  });

  return {
    dueCount: dueQuestions.length,
    plannedCount: Math.min(targetQuestionsPerDay, prioritized.length),
    questions: prioritized.slice(0, targetQuestionsPerDay),
    summary: {
      todaysFocus: dueQuestions.length > 0 ? 'Review overdue questions' : 'Learn new material',
    },
  };
}

// Get spaced repetition statistics
export function getSpacedRepetitionStats(allQuestions) {
  const stats = {
    dueToday: 0,
    overdue: 0,
    new: 0,
    mastered: 0,
    learning: 0,
  };

  const now = new Date();

  allQuestions.forEach(question => {
    const mastery = getQuestionMastery(question.id);

    stats[mastery.status] = (stats[mastery.status] || 0) + 1;

    if (isDueForReview(mastery)) {
      stats.dueToday += 1;

      const nextReview = getNextReviewDate(mastery);
      if (nextReview < now) {
        stats.overdue += 1;
      }
    }
  });

  return stats;
}

// Recommend which section to study next based on SR
export function getRecommendedSection(sections, allQuestionsBySection) {
  let bestSection = null;
  let bestScore = -1;

  sections.forEach(section => {
    const questions = allQuestionsBySection[section] || [];
    const dueQuestions = getDueQuestions(questions);
    const dueCount = dueQuestions.length;

    // Score is based on: overdue questions + new questions
    const score = dueCount + (questions.length - dueCount) * 0.5;

    if (score > bestScore) {
      bestScore = score;
      bestSection = section;
    }
  });

  return bestSection;
}
